import React, { useCallback, useMemo, useState } from 'react';
import {
    ActionButton,
    Button,
    ButtonGroup,
    Content,
    Dialog,
    DialogContainer,
    Divider,
    Footer,
    Heading,
    Item,
    Menu,
    MenuTrigger,
} from '@adobe/react-spectrum';
import ChevronDown from '@spectrum-icons/workflow/ChevronDown';
import { humanizeUpgradesEnum } from './labelUtils';
import './UpgradelizerAdvancedFilters.css';

/**
 * Advanced filter panel for the card_category /upgrades search surface.
 *
 * Exposes every additive filter introduced by the base-search overhaul —
 * oracle full-text (q), CMC / activation-cost ranges, priceMax,
 * keywords / subtypes / superTypes / producesMana, multi-value category,
 * excludeInDeck / combosWithInDeck, effect/action/zone/flag pickers for
 * both trigger and result sides, a qualifier-clause builder, and the
 * matchesPerCard / limit response-shape controls.
 *
 * State lives on the parent — this component is purely a controlled view.
 * The single `value` object mirrors the param surface; `onChange(patch)`
 * receives a partial update to merge.
 */

export const ADVANCED_FILTER_DEFAULTS = Object.freeze({
    q: '',
    extraCategories: [],
    cmcMin: '',
    cmcMax: '',
    priceMax: '',
    keywords: [],
    subtypes: [],
    superTypes: [],
    producesMana: [],
    combosWithInDeck: false,
    matchesPerCard: '',
    limit: '',
    effectTypes: [],
    actionScope: [],
    activationCmcMin: '',
    activationCmcMax: '',
    triggerActorType: [],
    triggerDependencyActorType: [],
    triggerActions: [],
    triggerDependencyActions: [],
    triggerCardActions: [],
    triggerZonesFrom: [],
    triggerZonesTo: [],
    triggerFlags: [],
    resultActorType: [],
    resultActions: [],
    resultCardActions: [],
    resultZonesFrom: [],
    resultZonesTo: [],
    resultFlags: [],
    qualifiers: [],
});

// ---- Option vocabularies ----

const EFFECT_TYPE_OPTIONS = ['trigger', 'triggerChoice', 'ability', 'static', 'replacement', 'castingCost', 'result'];
const ACTION_SCOPE_OPTIONS = ['all', 'multiple', 'single', 'modes_choose', 'choose', 'other', 'self'];
const ACTOR_TYPE_OPTIONS = ['self', 'all', 'opponent', 'target'];
const ZONE_OPTIONS = [
    'hand',
    'battlefield',
    'graveyard',
    'exile',
    'outsideOfGame',
    'command',
    'library',
    'stack',
    'anywhere',
    'opponents_library',
    'unspecified',
];
// Common starter lists for the tag inputs — users can type any value;
// these just power the suggestion chips below the input.
const PLAYER_ACTION_SUGGESTIONS = [
    'draw',
    'mill',
    'search',
    'cast',
    'gain_life',
    'lose_life',
    'discard',
    'sacrifice',
    'scry',
    'surveil',
    'create_token',
    'tap_for_mana',
    'pay_mana',
    'pay_life',
];
const CARD_ACTION_SUGGESTIONS = [
    'card_enters',
    'card_dies',
    'card_attacks',
    'card_blocks',
    'card_becomes_target',
    'card_tap_for_mana',
    'card_leaves',
    'card_cast',
];
// Allow-listed flag names — must match the backend's `ALLOWED_FLAG_NAMES`
// Set in `src/api/upgrades/sources/_searchBuilder.js`. Any other value
// returns 400 unknown_flag. Snapshot from upgrades-allowed-flags.md
// (2026-05-22); zero-on-both-sides flags (hasConspire, hasCrewCost,
// hasSaddleCost, isKeywordSynthetic) are intentionally excluded — the
// parser doesn't emit them yet, so showing them just wastes screen real
// estate. Add them back when the dataset catches up.
const FLAG_OPTIONS = [
    'hasYouMayCast',
    'hasYouMayPlay',
    'hasCopyTarget',
    'hasCopySpell',
    'hasAnyNumberOf',
    'hasGainControlOfTarget',
    'hasCreateTokenCopy',
    'hasDelve',
    'hasDelirium',
    'hasConvoke',
    'hasStorm',
    'hasImprovise',
    'hasAffinity',
    'hasReturnFromGraveyard',
    'hasEnchantInGraveyard',
    'hasExileFromGraveyard',
    'hasGrantsCastFromGraveyard',
    'checksGraveyard',
    'hasLifeThreshold',
    'checksSacrifices',
    'checksDies',
    'checksYouControl',
    'hasPutIntoGraveyard',
    'hasEquipCost',
    'hasGoadTrigger',
    'checksEnters',
    'hasPutOntoBattlefieldFromHand',
    'hasInsteadClause',
    'checksTokensAreCreated',
    'hasPlayAdditionalLand',
    'hasKeywordLandfall',
    'hasKeywordMiracle',
    'hasReturnToTheBattlefield',
    'hasReturnToHand',
    'hasPayLife',
    'hasWardEffect',
    'hasDredgeEffect',
    'checksSelfTap',
    'checksSelfUntap',
    'checksSelfLeavesBattlefield',
    'checksSelfDies',
    'hasSacrificeSelf',
    'isKeywordETBTrigger',
    'grantsAbility',
    'createsToken',
    'hasSacrificeFood',
];
const KEYWORD_SUGGESTIONS = [
    'flying',
    'lifelink',
    'deathtouch',
    'vigilance',
    'haste',
    'hexproof',
    'indestructible',
    'trample',
    'menace',
    'reach',
    'first_strike',
    'double_strike',
    'flash',
    'ward',
    'shroud',
    'defender',
];
const SUPERTYPE_OPTIONS = ['legendary', 'basic', 'snow', 'world', 'tribal'];
// Only the six concrete mana colors are valid filter inputs. The backend
// auto-expands a specific-color request to include `any`-producers
// (Birds of Paradise, Bloom Tender, …), so picking `G` already surfaces
// rainbow producers alongside Llanowar Elves — there's no separate
// "untyped" input to send. See §2.3.6 of the API doc.
const PRODUCES_MANA_OPTIONS = ['W', 'U', 'B', 'R', 'G', 'C'];

const QUALIFIER_KINDS = [
    { id: 'color', open: false },
    { id: 'superType', open: false },
    { id: 'devotion', open: false },
    { id: 'abilityType', open: false },
    { id: 'toughness_modality', open: false },
    { id: 'power_modality', open: false },
    { id: 'cmc_modality', open: false },
    { id: 'quantity', open: false },
    { id: 'nth', open: false },
    { id: 'power', open: false },
    { id: 'toughness', open: false },
    { id: 'manavalue', open: false },
    { id: 'abilityReduction', open: false },
    { id: 'costReduction', open: false },
    { id: 'type', open: true },
    { id: 'counter_type', open: true },
    { id: 'keyword', open: true },
    { id: 'keywordExclusion', open: true },
    { id: 'named', open: true },
];

const QUALIFIER_KIND_IDS = QUALIFIER_KINDS.map((k) => k.id);

const MAX_QUALIFIER_CLAUSES = 10;
const MAX_QUALIFIER_VALUES_PER_CLAUSE = 20;

// ---- Tiny controlled-input primitives (intentionally markup-light) ----

function FieldLabel({ children, hint }) {
    return (
        <span className="upgAdvLabel">
            <span className="upgAdvLabelText">{children}</span>
            {hint && <span className="upgAdvLabelHint">{hint}</span>}
        </span>
    );
}

function ChipMultiSelect({ label, hint, options, value, onChange }) {
    const set = useMemo(() => new Set(value || []), [value]);
    const toggle = useCallback(
        (id) => {
            const next = new Set(set);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            onChange([...next]);
        },
        [set, onChange]
    );
    return (
        <div className="upgAdvField">
            <FieldLabel hint={hint}>{label}</FieldLabel>
            <div className="upgAdvChips">
                {options.map((opt) => {
                    const id = typeof opt === 'string' ? opt : opt.id;
                    const rawLabel = typeof opt === 'string' ? opt : opt.label;
                    // Object options keep their hand-authored label; plain
                    // string options get the snake/camel → spaced form so
                    // chips read as English without rewriting every list.
                    const display = typeof opt === 'string' ? humanizeUpgradesEnum(rawLabel) : rawLabel;
                    const active = set.has(id);
                    return (
                        <button
                            key={id}
                            type="button"
                            className={`upgAdvChip${active ? ' is-active' : ''}`}
                            onClick={() => toggle(id)}
                            aria-pressed={active}
                        >
                            {display}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

/**
 * Compact multi-select dropdown for option lists too long for a chip
 * wall (Flags has ~46 entries). Uses Spectrum's MenuTrigger pattern to
 * match the rest of the pane (TypesControl, ComboControls). Selection
 * state round-trips as an array; Spectrum's onSelectionChange returns a
 * Set or the "all" sentinel — the sentinel is ignored since we don't
 * expose a "select all" affordance.
 */
function MultiSelectMenu({ label, hint, options, value, onChange, placeholder = 'None' }) {
    const selectedSet = useMemo(() => new Set(value || []), [value]);
    const items = useMemo(
        () => options.map((opt) => (typeof opt === 'string' ? { id: opt, label: humanizeUpgradesEnum(opt) } : opt)),
        [options]
    );
    const triggerLabel = (() => {
        if (selectedSet.size === 0) return placeholder;
        if (selectedSet.size === 1) {
            const onlyId = [...selectedSet][0];
            const found = items.find((it) => it.id === onlyId);
            return found?.label || onlyId;
        }
        return `${selectedSet.size} selected`;
    })();
    return (
        <div className="upgAdvField">
            <FieldLabel hint={hint}>{label}</FieldLabel>
            <MenuTrigger>
                <ActionButton UNSAFE_className="upgAdvMenuTrigger">
                    <span className="upgAdvMenuTriggerLabel">{triggerLabel}</span>
                    <ChevronDown size="XS" />
                </ActionButton>
                <Menu
                    items={items}
                    selectedKeys={selectedSet}
                    selectionMode="multiple"
                    onSelectionChange={(keys) => {
                        if (keys === 'all') return;
                        onChange([...keys]);
                    }}
                >
                    {(item) => <Item key={item.id}>{item.label}</Item>}
                </Menu>
            </MenuTrigger>
        </div>
    );
}

function TagInput({ label, hint, value, onChange, suggestions = [], placeholder = '' }) {
    const tags = value || [];
    const addTag = (raw) => {
        const next = String(raw || '')
            .trim()
            .toLowerCase();
        if (!next) return;
        if (tags.includes(next)) return;
        onChange([...tags, next]);
    };
    const removeTag = (tag) => onChange(tags.filter((t) => t !== tag));
    const handleKeyDown = (evn) => {
        if (evn.key === 'Enter' || evn.key === ',' || evn.key === 'Tab') {
            const raw = evn.target.value;
            if (raw.trim()) {
                evn.preventDefault();
                addTag(raw);
                evn.target.value = '';
            }
        } else if (evn.key === 'Backspace' && !evn.target.value && tags.length > 0) {
            removeTag(tags[tags.length - 1]);
        }
    };
    return (
        <div className="upgAdvField">
            <FieldLabel hint={hint}>{label}</FieldLabel>
            <div className="upgAdvTagInput">
                {tags.map((tag) => (
                    <span key={tag} className="upgAdvTag">
                        {humanizeUpgradesEnum(tag)}
                        <button
                            type="button"
                            className="upgAdvTagRemove"
                            onClick={() => removeTag(tag)}
                            aria-label={`Remove ${tag}`}
                        >
                            ×
                        </button>
                    </span>
                ))}
                <input
                    type="text"
                    className="upgAdvTagInputBox"
                    placeholder={tags.length === 0 ? placeholder : ''}
                    onKeyDown={handleKeyDown}
                    onBlur={(evn) => {
                        if (evn.target.value.trim()) {
                            addTag(evn.target.value);
                            evn.target.value = '';
                        }
                    }}
                />
            </div>
            {suggestions.length > 0 && (
                <div className="upgAdvSuggestions">
                    {suggestions.map((s) => {
                        const active = tags.includes(s);
                        return (
                            <button
                                key={s}
                                type="button"
                                className={`upgAdvSuggestion${active ? ' is-active' : ''}`}
                                onClick={() => (active ? removeTag(s) : addTag(s))}
                            >
                                {humanizeUpgradesEnum(s)}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function TextField({ label, hint, value, onChange, placeholder, maxLength }) {
    return (
        <div className="upgAdvField">
            <FieldLabel hint={hint}>{label}</FieldLabel>
            <input
                type="text"
                className="upgAdvText"
                value={value || ''}
                onChange={(evn) => onChange(evn.target.value)}
                placeholder={placeholder}
                maxLength={maxLength}
            />
        </div>
    );
}

function NumberField({ label, hint, value, onChange, min, max, placeholder }) {
    return (
        <div className="upgAdvField upgAdvField--narrow">
            <FieldLabel hint={hint}>{label}</FieldLabel>
            <input
                type="number"
                className="upgAdvText"
                value={value}
                min={min}
                max={max}
                placeholder={placeholder}
                onChange={(evn) => onChange(evn.target.value.replace(/[^0-9.]/g, ''))}
            />
        </div>
    );
}

function CheckField({ label, hint, value, onChange }) {
    return (
        <label className="upgAdvCheck">
            <input type="checkbox" checked={!!value} onChange={(evn) => onChange(evn.target.checked)} />
            <span className="upgAdvCheckLabel">{label}</span>
            {hint && <span className="upgAdvLabelHint">{hint}</span>}
        </label>
    );
}

// ---- Qualifier clause builder ----

function QualifierBuilder({ value, onChange }) {
    const clauses = value || [];
    const updateClause = (idx, patch) => {
        const next = clauses.slice();
        next[idx] = { ...next[idx], ...patch };
        onChange(next);
    };
    const removeClause = (idx) => onChange(clauses.filter((_, i) => i !== idx));
    const addClause = () => {
        if (clauses.length >= MAX_QUALIFIER_CLAUSES) return;
        onChange([...clauses, { side: 'result', kind: 'type', values: [] }]);
    };
    return (
        <div className="upgAdvField upgAdvField--full">
            <FieldLabel
                hint={`Up to ${MAX_QUALIFIER_CLAUSES} clauses · ${MAX_QUALIFIER_VALUES_PER_CLAUSE} values each`}
            >
                Qualifiers
            </FieldLabel>
            <div className="upgAdvQualifierList">
                {clauses.length === 0 && (
                    <div className="upgAdvQualifierEmpty">
                        No qualifier clauses. Add one to filter on the SUBJECT of a match (e.g. result.kind=quantity
                        values=[2,3] → "draw 2 or 3 cards").
                    </div>
                )}
                {clauses.map((clause, idx) => (
                    <QualifierClauseRow
                        key={idx}
                        clause={clause}
                        onChange={(patch) => updateClause(idx, patch)}
                        onRemove={() => removeClause(idx)}
                    />
                ))}
            </div>
            <button
                type="button"
                className="upgAdvAddBtn"
                onClick={addClause}
                disabled={clauses.length >= MAX_QUALIFIER_CLAUSES}
            >
                + Add qualifier clause
            </button>
        </div>
    );
}

function QualifierClauseRow({ clause, onChange, onRemove }) {
    const setValues = (vals) => {
        const capped = vals.slice(0, MAX_QUALIFIER_VALUES_PER_CLAUSE);
        onChange({ values: capped });
    };
    return (
        <div className="upgAdvQualifierClause">
            <select
                className="upgAdvSelect"
                value={clause.side || 'result'}
                onChange={(evn) => onChange({ side: evn.target.value })}
                aria-label="Qualifier side"
            >
                <option value="trigger">trigger</option>
                <option value="result">result</option>
            </select>
            <select
                className="upgAdvSelect"
                value={clause.kind || 'type'}
                onChange={(evn) => onChange({ kind: evn.target.value })}
                aria-label="Qualifier kind"
            >
                {QUALIFIER_KIND_IDS.map((kind) => (
                    <option key={kind} value={kind}>
                        {humanizeUpgradesEnum(kind)}
                    </option>
                ))}
            </select>
            <div className="upgAdvQualifierValues">
                <TagInput
                    label=""
                    value={clause.values || []}
                    onChange={setValues}
                    placeholder="values (enter or comma to add)"
                />
            </div>
            <button
                type="button"
                className="upgAdvRemoveBtn"
                onClick={onRemove}
                aria-label="Remove clause"
                title="Remove clause"
            >
                ×
            </button>
        </div>
    );
}

// ---- Section helper ----

function Section({ title, children, defaultOpen = false }) {
    return (
        <details className="upgAdvSection" open={defaultOpen}>
            <summary className="upgAdvSectionSummary">{title}</summary>
            <div className="upgAdvSectionBody">{children}</div>
        </details>
    );
}

// ---- Main component ----

/**
 * Modal wrapper. Spectrum's DialogContainer mounts/unmounts the dialog
 * body on `isOpen`, so the body's draft state is freshly seeded from the
 * applied `value` every time the modal opens. On mobile it takes over
 * the full screen; on desktop it's a centered modal.
 */
export function UpgradelizerAdvancedFilters({ isOpen, value, onApply, onClose, extraCategoryOptions = [], mobile }) {
    // Always a modal (never fullscreen): Spectrum relocates the
    // ButtonGroup into the header for fullscreen/fullscreenTakeover
    // dialogs, which drops the bottom Cancel/Apply/Reset row. A modal
    // keeps Footer (Reset) bottom-left and ButtonGroup (Cancel/Apply)
    // bottom-right on every screen size; on mobile it fills the viewport.
    //
    // NOT isDismissable: React Spectrum hides the action ButtonGroup
    // (display:none) on dismissable dialogs and renders an X instead, so
    // Cancel/Apply vanish. Escape still routes through onDismiss; closing
    // is via Cancel (or Apply), which also avoids losing the draft to an
    // accidental click-outside.
    return (
        <DialogContainer type="modal" onDismiss={onClose}>
            {isOpen && (
                <AdvancedFiltersDialogBody
                    value={value}
                    onApply={onApply}
                    onClose={onClose}
                    extraCategoryOptions={extraCategoryOptions}
                    mobile={mobile}
                />
            )}
        </DialogContainer>
    );
}

/**
 * Dialog body. Holds the DRAFT filter state — edits here do not touch the
 * applied filters (and therefore don't refire the search) until "Apply"
 * is pressed. Cancel discards the draft; Reset clears the draft back to
 * defaults (still not applied until Apply).
 */
function AdvancedFiltersDialogBody({ value, onApply, onClose, extraCategoryOptions, mobile }) {
    const [draft, setDraft] = useState(value || ADVANCED_FILTER_DEFAULTS);
    const set = (key) => (next) => setDraft((prev) => ({ ...prev, [key]: next }));
    const resetDraft = () => setDraft(ADVANCED_FILTER_DEFAULTS);
    const apply = () => onApply(draft);

    return (
        <Dialog
            UNSAFE_className="upgAdvDialog"
            size={mobile ? undefined : 'L'}
            height={mobile ? 'calc(100vh - 24px)' : '80vh'}
        >
            <Heading UNSAFE_className="upgAdvDialogHeading">Advanced filters</Heading>
            <Divider />
            <Content UNSAFE_className="upgAdvDialogContent">
                <div className="upgAdv">
                    <Section title="Card filters" defaultOpen>
                        <TextField
                            label="Oracle text"
                            hint="Exact phrase match · for ETB use Card Actions › card_enters"
                            value={draft.q}
                            onChange={set('q')}
                            placeholder="e.g. whenever you cast a noncreature spell"
                            maxLength={200}
                        />
                        <div className="upgAdvRow">
                            <NumberField
                                label="CMC min"
                                value={draft.cmcMin}
                                onChange={set('cmcMin')}
                                min={0}
                                max={99}
                                placeholder="0"
                            />
                            <NumberField
                                label="CMC max"
                                value={draft.cmcMax}
                                onChange={set('cmcMax')}
                                min={0}
                                max={99}
                                placeholder="99"
                            />
                            <NumberField
                                label="Price max ($)"
                                value={draft.priceMax}
                                onChange={set('priceMax')}
                                min={0}
                                placeholder="any"
                            />
                        </div>
                        <TagInput
                            label="Keywords"
                            value={draft.keywords}
                            onChange={set('keywords')}
                            suggestions={KEYWORD_SUGGESTIONS}
                            placeholder="flying, lifelink…"
                        />
                        <ChipMultiSelect
                            label="Super types"
                            options={SUPERTYPE_OPTIONS}
                            value={draft.superTypes}
                            onChange={set('superTypes')}
                        />
                        <TagInput
                            label="Subtypes"
                            hint="Creature/permanent subtypes (open-ended)"
                            value={draft.subtypes}
                            onChange={set('subtypes')}
                            placeholder="elf, warrior, vehicle…"
                        />
                        <ChipMultiSelect
                            label="Produces mana"
                            hint="Specific colors include rainbow producers automatically"
                            options={PRODUCES_MANA_OPTIONS}
                            value={draft.producesMana}
                            onChange={set('producesMana')}
                        />
                        {extraCategoryOptions.length > 0 && (
                            <ChipMultiSelect
                                label="Additional categories (multi-select)"
                                hint="Union with the primary category"
                                options={extraCategoryOptions}
                                value={draft.extraCategories}
                                onChange={set('extraCategories')}
                            />
                        )}
                        <CheckField
                            label="Only cards that complete a combo in the deck"
                            hint="Capped at 2000 cardIds — narrow if 400s"
                            value={draft.combosWithInDeck}
                            onChange={set('combosWithInDeck')}
                        />
                    </Section>

                    <Section title="Effect filters">
                        <ChipMultiSelect
                            label="Effect type"
                            options={EFFECT_TYPE_OPTIONS}
                            value={draft.effectTypes}
                            onChange={set('effectTypes')}
                        />
                        <ChipMultiSelect
                            label="Action scope"
                            options={ACTION_SCOPE_OPTIONS}
                            value={draft.actionScope}
                            onChange={set('actionScope')}
                        />
                        <div className="upgAdvRow">
                            <NumberField
                                label="Activation CMC min"
                                value={draft.activationCmcMin}
                                onChange={set('activationCmcMin')}
                                min={0}
                                max={99}
                                placeholder="0"
                            />
                            <NumberField
                                label="Activation CMC max"
                                value={draft.activationCmcMax}
                                onChange={set('activationCmcMax')}
                                min={0}
                                max={99}
                                placeholder="99"
                            />
                        </div>
                    </Section>

                    <Section title="Trigger condition">
                        <ChipMultiSelect
                            label="Player"
                            options={ACTOR_TYPE_OPTIONS}
                            value={draft.triggerActorType}
                            onChange={set('triggerActorType')}
                        />
                        <ChipMultiSelect
                            label="Dependency player"
                            options={ACTOR_TYPE_OPTIONS}
                            value={draft.triggerDependencyActorType}
                            onChange={set('triggerDependencyActorType')}
                        />
                        <TagInput
                            label="Player actions"
                            value={draft.triggerActions}
                            onChange={set('triggerActions')}
                            suggestions={PLAYER_ACTION_SUGGESTIONS}
                        />
                        <TagInput
                            label="Dependency actions"
                            value={draft.triggerDependencyActions}
                            onChange={set('triggerDependencyActions')}
                            suggestions={PLAYER_ACTION_SUGGESTIONS}
                        />
                        <TagInput
                            label="Card actions"
                            value={draft.triggerCardActions}
                            onChange={set('triggerCardActions')}
                            suggestions={CARD_ACTION_SUGGESTIONS}
                        />
                        <ChipMultiSelect
                            label="Zones from"
                            options={ZONE_OPTIONS}
                            value={draft.triggerZonesFrom}
                            onChange={set('triggerZonesFrom')}
                        />
                        <ChipMultiSelect
                            label="Zones to"
                            options={ZONE_OPTIONS}
                            value={draft.triggerZonesTo}
                            onChange={set('triggerZonesTo')}
                        />
                        {/* Flags picker temporarily hidden — backend allow-list
                            needs a friendlier presentation before exposing.
                            State + FLAG_OPTIONS + param plumbing are intact;
                            re-enable by rendering MultiSelectMenu for
                            draft.triggerFlags / draft.resultFlags. */}
                    </Section>

                    <Section title="Result (what the card does)">
                        <ChipMultiSelect
                            label="Player"
                            options={ACTOR_TYPE_OPTIONS}
                            value={draft.resultActorType}
                            onChange={set('resultActorType')}
                        />
                        <TagInput
                            label="Player actions"
                            value={draft.resultActions}
                            onChange={set('resultActions')}
                            suggestions={PLAYER_ACTION_SUGGESTIONS}
                        />
                        <TagInput
                            label="Card actions"
                            value={draft.resultCardActions}
                            onChange={set('resultCardActions')}
                            suggestions={CARD_ACTION_SUGGESTIONS}
                        />
                        <ChipMultiSelect
                            label="Zones from"
                            options={ZONE_OPTIONS}
                            value={draft.resultZonesFrom}
                            onChange={set('resultZonesFrom')}
                        />
                        <ChipMultiSelect
                            label="Zones to"
                            options={ZONE_OPTIONS}
                            value={draft.resultZonesTo}
                            onChange={set('resultZonesTo')}
                        />
                        {/* Flags picker temporarily hidden — see note above. */}
                    </Section>

                    <Section title="Qualifiers (advanced subject matching)">
                        <QualifierBuilder value={draft.qualifiers} onChange={set('qualifiers')} />
                    </Section>

                    <Section title="Response shape">
                        <div className="upgAdvRow">
                            <NumberField
                                label="Matches per card (1–25)"
                                value={draft.matchesPerCard}
                                onChange={set('matchesPerCard')}
                                min={1}
                                max={25}
                                placeholder="5"
                            />
                            <NumberField
                                label="Page size (1–100)"
                                value={draft.limit}
                                onChange={set('limit')}
                                min={1}
                                max={100}
                                placeholder="50"
                            />
                        </div>
                    </Section>
                </div>
            </Content>
            <Footer UNSAFE_className="upgAdvDialogFooter">
                <Button variant="secondary" onPress={resetDraft}>
                    Reset
                </Button>
            </Footer>
            <ButtonGroup>
                <Button variant="secondary" onPress={onClose}>
                    Cancel
                </Button>
                <Button variant="accent" onPress={apply}>
                    Apply
                </Button>
            </ButtonGroup>
        </Dialog>
    );
}

/**
 * Translate the advanced-filter state object into the param keys the
 * `/upgrades` endpoint accepts. Empty / default values are omitted so
 * cache keys stay byte-identical to the baseline request when no
 * advanced filter is active.
 */
export function advancedFiltersToParams(adv) {
    if (!adv) return {};
    const out = {};
    const q = (adv.q || '').trim();
    if (q) out.q = q.slice(0, 200);

    const numField = (key, parser = parseFloat) => {
        const n = parser(adv[key]);
        if (Number.isFinite(n) && n >= 0) out[key] = n;
    };
    numField('cmcMin', (v) => parseInt(v, 10));
    numField('cmcMax', (v) => parseInt(v, 10));
    numField('priceMax');
    numField('activationCmcMin', (v) => parseInt(v, 10));
    numField('activationCmcMax', (v) => parseInt(v, 10));

    const arrField = (key) => {
        if (Array.isArray(adv[key]) && adv[key].length > 0) out[key] = adv[key];
    };
    [
        'keywords',
        'subtypes',
        'superTypes',
        'producesMana',
        'effectTypes',
        'actionScope',
        'triggerActorType',
        'triggerDependencyActorType',
        'triggerActions',
        'triggerDependencyActions',
        'triggerCardActions',
        'triggerZonesFrom',
        'triggerZonesTo',
        'triggerFlags',
        'resultActorType',
        'resultActions',
        'resultCardActions',
        'resultZonesFrom',
        'resultZonesTo',
        'resultFlags',
    ].forEach(arrField);

    if (adv.combosWithInDeck === true) out.combosWithInDeck = true;

    const mpc = parseInt(adv.matchesPerCard, 10);
    if (Number.isFinite(mpc) && mpc >= 1) out.matchesPerCard = Math.min(25, mpc);
    const lim = parseInt(adv.limit, 10);
    if (Number.isFinite(lim) && lim >= 1) out.limit = Math.min(100, lim);

    const cleanedQualifiers = (adv.qualifiers || [])
        .filter((c) => c && c.side && c.kind && Array.isArray(c.values) && c.values.length > 0)
        .slice(0, MAX_QUALIFIER_CLAUSES)
        .map((c) => ({
            side: c.side,
            kind: c.kind,
            values: c.values.slice(0, MAX_QUALIFIER_VALUES_PER_CLAUSE),
        }));
    if (cleanedQualifiers.length > 0) out.qualifiers = cleanedQualifiers;

    return out;
}

/**
 * Count how many advanced filter dimensions are currently non-default —
 * used for the summary badge so the user sees activity without opening
 * the panel.
 */
export function countActiveAdvancedFilters(adv) {
    if (!adv) return 0;
    let n = 0;
    if ((adv.q || '').trim()) n += 1;
    ['cmcMin', 'cmcMax', 'priceMax', 'activationCmcMin', 'activationCmcMax', 'matchesPerCard', 'limit'].forEach((k) => {
        if (adv[k] !== '' && adv[k] != null && !Number.isNaN(parseFloat(adv[k]))) n += 1;
    });
    [
        'keywords',
        'subtypes',
        'superTypes',
        'producesMana',
        'extraCategories',
        'effectTypes',
        'actionScope',
        'triggerActorType',
        'triggerDependencyActorType',
        'triggerActions',
        'triggerDependencyActions',
        'triggerCardActions',
        'triggerZonesFrom',
        'triggerZonesTo',
        'triggerFlags',
        'resultActorType',
        'resultActions',
        'resultCardActions',
        'resultZonesFrom',
        'resultZonesTo',
        'resultFlags',
    ].forEach((k) => {
        if (Array.isArray(adv[k]) && adv[k].length > 0) n += 1;
    });
    if (adv.combosWithInDeck === true) n += 1;
    if ((adv.qualifiers || []).some((c) => c?.values?.length > 0)) n += 1;
    return n;
}
